import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { fg } from '@confluence/feature-gating';

import type { FireOnceGuard } from './createFireOnceGuard.ts';
import { matchesAnyKeyword } from './matchKeywords.ts';
import { matchesMeetingTemplate } from './meetingTemplates.ts';
import type {
	MeetingSignalSource,
	MeetingSignalTrigger,
	TemplateIdAttributes,
} from './meetingSignalTypes.ts';

/** Fires a Post Office trigger — shape matches useFirePostOfficeTrigger's fireTrigger. */
export type FireMeetingNudgeTrigger = (state: { isActive: boolean }) => void;

export interface EvaluateMeetingSignalParams {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	source: MeetingSignalSource;
	trigger: MeetingSignalTrigger;
	/** In-memory fire-once guard for this surface's page load. */
	guard?: FireOnceGuard;
	/** Title text + the resolved title keyword list (title signal). */
	titleText?: string | null;
	titleKeywords?: readonly string[];
	/** Body plain text + the resolved content keyword list (content signal). */
	contentText?: string | null;
	contentKeywords?: readonly string[];
	/** Selected template identity (template signal, create/edit time only). */
	template?: TemplateIdAttributes | null;
	/** Fires the title / side-nav PO nudge trigger (twcg-2202-trigger-prototype). */
	fireTitleTrigger?: FireMeetingNudgeTrigger;
	fireContentTrigger?: FireMeetingNudgeTrigger;
}

/**
 * The shared detection pipeline, applied at every surface:
 *
 * 1. Gate — bail unless the Statsig gate is on (default off; dark-launched).
 *    Exact key TBD (STARB-3763); passed as a static literal so the
 *    `confluence-feature-gating/static-feature-gates` lint rule is satisfied.
 * 2. Fire-once — bail if this surface already fired for this page load.
 * 3. Match — title/content lists (whole-word) and/or the meeting-template set.
 * 4. Check — whether any signal matched now.
 * 5. Fire — only on a positive match, then mark the guard. The single GAS v3
 *    `meetingSignal detected` track event carries only `source` + `trigger`.
 *    No keyword text/count, template id, locale, page/space id, or page content
 *    is ever sent. `source` + `trigger` is the full analytical payload; the
 *    matched signals are checked only to decide whether to fire.
 *
 * Returns `true` if it fired the event, `false` otherwise (no match, gate off,
 * or already fired). Callers can pass whichever of title/content/template
 * inputs are knowable at their trigger.
 */
export const evaluateAndMaybeFireMeetingSignal = (params: EvaluateMeetingSignalParams): boolean => {
	const {
		createAnalyticsEvent,
		source,
		trigger,
		guard,
		titleText,
		titleKeywords,
		contentText,
		contentKeywords,
		template,
		fireTitleTrigger,
		fireContentTrigger,
	} = params;

	const titleMatched =
		!!titleText && !!titleKeywords && matchesAnyKeyword(titleText, titleKeywords);
	const contentMatched =
		!!contentText && !!contentKeywords && matchesAnyKeyword(contentText, contentKeywords);
	const templateMatched = !!template && matchesMeetingTemplate(template);

	// Fire the explicit PO nudge trigger for whichever surface matched: a title match
	// shows the title nudge, a content match the side-nav nudge. Gated independently
	// from the analytics gate below.
	if (fg('twcg-2202-trigger-prototype')) {
		if (titleMatched) {
			fireTitleTrigger?.({ isActive: true });
		}
		if (contentMatched) {
			fireContentTrigger?.({ isActive: true });
		}
	}

	if (!fg('twcg-2202-gasv3')) {
		return false;
	}

	if (guard?.hasFired()) {
		return false;
	}

	if (!titleMatched && !contentMatched && !templateMatched) {
		return false;
	}

	guard?.markFired();
	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'detected',
			actionSubject: 'meetingSignal',
			source,
			attributes: {
				trigger,
			},
		},
	}).fire();

	return true;
};
